import { useEffect, useMemo, useState } from 'react'
import { api, type QualityIssue } from '../api'
import { useApp } from '../context'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Badge, Empty, ErrorState, Loading, Section } from './ui'

const SEVERITY_TONE = { high: 'danger', medium: 'warn', low: 'neutral' } as const
const KIND_LABEL: Record<string, string> = { untraceable: 'Not traceable', 'rx-deviation': 'Low light', budget: 'Budget', 'no-reading': 'No reading' }

export function QualityPanel() {
  const { open, setHighlight } = useApp()
  const q = useAsync(() => api.quality(), [])
  const [kind, setKind] = useState<string>('all')
  const issues = useMemo(() => q.data?.issues ?? [], [q.data])
  const kinds = useMemo(() => [...new Set(issues.map((i) => i.kind))], [issues])
  const shown = useMemo(() => (kind === 'all' ? issues : issues.filter((i) => i.kind === kind)), [issues, kind])

  useEffect(() => {
    setHighlight({ nodeIds: [...new Set(shown.flatMap((i) => (i.nodeId ? [i.nodeId] : [])))], tone: 'danger' })
  }, [shown, setHighlight])

  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading />
  const go = (i: QualityIssue) => (i.serviceId && i.kind !== 'untraceable' ? open({ kind: 'trace', serviceId: i.serviceId }) : i.nodeId && open({ kind: 'node', id: i.nodeId }))

  return (
    <PanelShell title="Data quality" subtitle={`${issues.length} open issue${issues.length === 1 ? '' : 's'}, most serious first`}>
      <div className="flex flex-wrap gap-1 px-4 pt-3" role="radiogroup" aria-label="Filter issues">
        {['all', ...kinds].map((k) => (
          <button
            key={k}
            role="radio"
            aria-checked={kind === k}
            onClick={() => setKind(k)}
            className={`rounded-md px-2 py-1 text-xs font-medium ${kind === k ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted hover:text-fg'}`}
          >
            {k === 'all' ? 'All' : KIND_LABEL[k] ?? k} {k === 'all' ? issues.length : issues.filter((i) => i.kind === k).length}
          </button>
        ))}
      </div>
      <Section title="Issues">
        {!shown.length && <Empty title="No issues">Every subscriber traces to an OLT and reads within design.</Empty>}
        <ul className="space-y-2">
          {shown.map((i, idx) => (
            <li key={idx}>
              <button onClick={() => go(i)} className="w-full rounded-lg border border-border p-2.5 text-left text-sm hover:bg-surface-2">
                <div className="flex items-center gap-2">
                  <Badge tone={SEVERITY_TONE[i.severity]}>{i.severity}</Badge>
                  <span className="text-xs text-muted">{KIND_LABEL[i.kind] ?? i.kind}</span>
                </div>
                <div className="mt-1 font-medium">{i.title}</div>
                <div className="mt-0.5 text-muted">{i.detail}</div>
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </PanelShell>
  )
}
